import {Errors, KAError, logUnreachableCode} from "@khan/logging";

import type {
    Program,
    ProgramSettings,
    UserAuthoredContentType,
} from "../scratchpad-ui.tsx";

import type {Program as GqlProgram} from "./__graphql-types__/Program.ts";
import type {spinOffProgramType} from "./__graphql-types__/spinOffProgram.ts";
import type {updateProgramSettings_updateProgramSettings_program as GqlProgramSettings} from "./__graphql-types__/updateProgramSettings.ts";

type GqlContentType = GqlProgram["userAuthoredContentType"];
type GqlSpinoffOriginKind =
    spinOffProgramType["variables"]["originProgramKind"];

/**
 * Helper function that maps a GraphQL UserAuthoredContentType (e.g. "PJS")
 * to the type expected by our legacy frontend code (e.g. "pjs").
 */
export const mapToContentType = (
    contentType: GqlContentType,
): UserAuthoredContentType => {
    switch (contentType) {
        case "PJS":
            return "pjs";
        case "SQL":
            return "sql";
        case "WEBPAGE":
            return "webpage";
        case "PYTHON":
            throw new KAError(
                "Python programs are not supported in scratchpad UI",
                Errors.Internal,
            );
        default:
            throw logUnreachableCode("Unexpected content type", contentType);
    }
};

/**
 * Helper function that maps a legacy UserAuthoredContentType (e.g. "pjs")
 * to the enum expected by GraphQL (e.g. "PJS")
 */
export const mapToGqlContentType = (
    contentType: UserAuthoredContentType,
): GqlContentType => {
    switch (contentType) {
        case "pjs":
            return "PJS";
        case "sql":
            return "SQL";
        case "webpage":
            return "WEBPAGE";
        default:
            throw logUnreachableCode("Unexpected content type", contentType);
    }
};

/**
 * The subset of LearnableContentKinds that can be spun off into new Programs
 */
export type SpinoffOriginKind =
    | "Challenge"
    | "Interactive"
    | "Program"
    | "Scratchpad"
    | "Project"
    | "Talkthrough";

/**
 * Helper function that maps a LearnableContentKind (e.g. "Challenge") to the
 * SpinoffOriginKind expected by GraphQL (e.g. "CHALLENGE")
 */
export const mapToGqlSpinoffOriginKind = (
    originKind: SpinoffOriginKind,
): GqlSpinoffOriginKind => {
    switch (originKind) {
        case "Challenge":
            return "CHALLENGE";
        case "Interactive":
            return "INTERACTIVE";
        case "Program":
        case "Scratchpad":
            return "PROGRAM";
        case "Project":
            return "PROJECT";
        case "Talkthrough":
            return "TALKTHROUGH";
        default:
            throw logUnreachableCode(
                "Unexpected spinoff origin kind",
                originKind,
            );
    }
};

/**
 * Helper function that maps a GraphQL Program to the object expected by our
 * legacy frontend code.
 */
export const mapToProgram = (program: GqlProgram): Program => {
    return {
        id: program.id,
        revision: {
            code: program.latestRevision?.code,
        },
        relativeUrl: program.url,
        title: program.title,
        userAuthoredContentType: mapToContentType(
            program.userAuthoredContentType,
        ),
    };
};

/**
 * Helper function for mapping GraphQL Program fields related to settings
 * to a ProgramSettings type for the frontend to use.
 */
export const mapToProgramSettings = (
    program: GqlProgramSettings,
): ProgramSettings => {
    return {
        category: program.category,
        description: program.description,
        height: program.height,
        width: program.width,
    };
};
